// The artifacts browser's three per-row acts — the state half of
// `routes/Artifacts`, split out the way `createMachineRemove` and
// `lib/donationInbox.ts` are: a mutation with a confirm step, a per-row busy key
// and a failure notice is STATE, not rendering, and it unit-tests without a shell.
//
// Four rules:
//
//   - **Busy is keyed BY ARTIFACT.** Three rows are three independent calls; a
//     single "which row is working" slot would re-enable row A the moment row B
//     started, which is how a reader double-fires a delete.
//   - **A download confirms nothing and re-reads nothing.** `GET …/url` mints a
//     link and changes no state, so treating it like the two writes would put a
//     confirm in front of the one act that is free to repeat.
//   - **Share and delete both CONFIRM, and both re-READ.** Share flips
//     `visibility` to `public-link` durably, audits it, and notifies every member;
//     NO surface un-shares an artifact, so the only way back is deleting it.
//     Delete removes the bytes. Neither is recoverable from this screen, and the
//     row's state after either is the server's answer, never the echo.
//   - **A dead link is dismissed with the artifact it addressed.** Deleting the
//     row a presigned URL points at leaves that URL on screen still looking live;
//     it is cleared with the row so nobody hands out a 404.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { type ApiArtifact, artifactDownloadUrl, deleteArtifact, shareArtifact } from './artifacts';
import { createKeyedMutation } from './keyed-mutation';

/** A live presigned URL, and which act produced it. Rendered once, then dismissed. */
export type ArtifactLink = {
  readonly kind: 'download' | 'share';
  readonly artifact: ApiArtifact;
  readonly url: string;
  readonly expiresInSeconds: number;
};

/** The act awaiting confirmation. One slot: `<ConfirmDialog/>` shows one at a time. */
export type PendingArtifactAction = {
  readonly kind: 'share' | 'delete';
  readonly artifact: ApiArtifact;
};

export type ArtifactActions = {
  /** True while THIS artifact has a call in flight. */
  readonly isBusy: (id: string) => boolean;
  /** The presigned URL just minted, or null. Feed to `<ArtifactLinkPanel/>`. */
  readonly link: () => ArtifactLink | null;
  readonly dismissLink: () => void;
  /** Failure copy for the surface's `<Toast/>`, or null. */
  readonly error: () => string | null;
  readonly clearError: () => void;
  /** The share/delete awaiting confirmation, or null. Feed to `<ConfirmDialog/>`. */
  readonly pending: () => PendingArtifactAction | null;
  /** Mint a download link. No confirm — it mutates nothing. */
  readonly download: (artifact: ApiArtifact) => void;
  readonly askShare: (artifact: ApiArtifact) => void;
  readonly askDelete: (artifact: ApiArtifact) => void;
  /** Dismiss the confirm without acting. */
  readonly cancel: () => void;
  /** Run whichever act is under confirmation. A second call mid-flight is dropped. */
  readonly confirm: () => void;
};

/**
 * Action state for one artifacts screen. `token` is passed to every call rather
 * than defaulted here so a test drives it without a session, and `onWritten` is
 * the RE-READ both writes end in — nothing patches a row from a response body.
 */
export function createArtifactActions(input: {
  readonly token: string | null;
  readonly onWritten: () => void;
}): ArtifactActions {
  const [link, setLink] = createSignal<ArtifactLink | null>(null);
  const [pending, setPending] = createSignal<PendingArtifactAction | null>(null);
  // One busy key per ARTIFACT, shared by all three acts — `createKeyedMutation`
  // takes the call per invocation for exactly that: a mutation per act would
  // give each its own key and let a reader delete the row a share is writing.
  const act = createKeyedMutation({ fallback: t('artifacts.action_failed') });

  /** One in-flight call per artifact, with the busy flag released either way. */
  const run = (artifact: ApiArtifact, call: () => Promise<void>): void => {
    void act.run(artifact.id, call);
  };

  const download = (artifact: ApiArtifact): void =>
    run(artifact, async () => {
      const access = await artifactDownloadUrl(artifact.id, input.token);
      setLink({
        kind: 'download',
        artifact,
        url: access.url,
        expiresInSeconds: access.expires_in_seconds,
      });
    });

  const confirm = (): void => {
    const act = pending();
    if (act === null) return;
    const { artifact } = act;
    if (act.kind === 'share') {
      run(artifact, async () => {
        const access = await shareArtifact(artifact.id, input.token);
        setLink({
          kind: 'share',
          artifact,
          url: access.url,
          expiresInSeconds: access.expires_in_seconds,
        });
        // Close only the confirm THIS call opened — nothing stops the reader
        // dismissing it and asking about another row mid-flight.
        if (pending() === act) setPending(null);
        input.onWritten();
      });
      return;
    }
    run(artifact, async () => {
      await deleteArtifact(artifact.id, input.token);
      if (pending() === act) setPending(null);
      // The bytes are gone; a link still pointing at them would 404 on click.
      if (link()?.artifact.id === artifact.id) setLink(null);
      input.onWritten();
    });
  };

  return {
    isBusy: act.isBusy,
    link,
    dismissLink: () => setLink(null),
    error: act.error,
    clearError: act.clearError,
    pending,
    download,
    askShare: (artifact) => {
      act.clearError();
      setPending({ kind: 'share', artifact });
    },
    askDelete: (artifact) => {
      act.clearError();
      setPending({ kind: 'delete', artifact });
    },
    cancel: () => setPending(null),
    confirm,
  };
}
